﻿import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useWorkflowLookups } from "../WorkflowLookupsContext";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import { WorkflowLookupMode } from "@/components/Maintenance/MaintenanceTypes";

const labelSx = { mr: 3, minWidth: 280, alignItems: "right" };

const AddImpliedDCPopup = ({
  open,
  onClose,
  workflowLookupType,
  fetchData,
  mode = WorkflowLookupMode.Add,
  existingRow = null,
}) => {
  const dispatch = useDispatch();

  const {
    fetchCatalogArtifactData,
    customerConnections,
    customersLoaded,
    ERPConnections,
    rows,
  } = useWorkflowLookups();

  // Selected objects for Autocompletes
  const [selectedCustomerConnection, setSelectedCustomerConnection] =
    useState(null);
  const [selectedStoreShipTo, setSelectedStoreShipTo] = useState(null);
  const [
    selectedDistributionCentreShipTo,
    setSelectedDistributionCentreShipTo,
  ] = useState(null);
  const [customerConnectionShipTos, setCustomerConnectionShipTos] = useState(
    []
  );
  const [shipTosLoading, setShipTosLoading] = useState(false);

  // Prefill when editing
  useEffect(() => {
    if (!open) return;

    if (mode === WorkflowLookupMode.Edit && existingRow) {
      const conn =
        customerConnections.find(
          (c) => c.ConnectionId === existingRow.CustomerConnectionId
        ) || null;
      setSelectedCustomerConnection(conn);
    } else if (mode === WorkflowLookupMode.Add) {
      setSelectedCustomerConnection(null);
      setSelectedStoreShipTo(null);
      setSelectedDistributionCentreShipTo(null);
      setCustomerConnectionShipTos([]);
    }
  }, [open]);

  // Fetch Customer ShipTos when CustomerConnection changes
  useEffect(() => {
    if (selectedCustomerConnection?.ConnectionId != null) {
      setShipTosLoading(true);
      fetchCatalogArtifactData({
        connectionId: selectedCustomerConnection.ConnectionId,
        catalogType: CatalogTypes.ShipTo,
      })
        .then((shipTosData) => setCustomerConnectionShipTos(shipTosData))
        .catch(() => setCustomerConnectionShipTos([]))
        .finally(() => setShipTosLoading(false));
    } else {
      setCustomerConnectionShipTos([]);
      setShipTosLoading(false);
    }
  }, [selectedCustomerConnection, fetchCatalogArtifactData]);

  // After ship-tos load, set the selected store/DC in edit mode
  useEffect(() => {
    if (
      mode === WorkflowLookupMode.Edit &&
      existingRow &&
      customerConnectionShipTos?.length > 0
    ) {
      const store = customerConnectionShipTos.find(
        (s) => s.ShipToCode === existingRow.StoreShipToCode
      );
      const dc = customerConnectionShipTos.find(
        (s) => s.ShipToCode === existingRow.DistributionCentreShipToCode
      );
      setSelectedStoreShipTo(store || null);
      setSelectedDistributionCentreShipTo(dc || null);
    }
  }, [mode, existingRow, customerConnectionShipTos]);

  // Helper for code-first label
  const codeFirstLabel = (code, name) => {
    if (!code) return name || "";
    if (name && name !== "" && name !== code) return `${code} - ${name}`;
    return code;
  };

  const validateForm = () => {
    if (!selectedCustomerConnection?.ConnectionId) {
      return {
        isValid: false,
        message: "Please select a Customer Connection before saving.",
      };
    }
    if (!selectedStoreShipTo?.ShipToCode) {
      return {
        isValid: false,
        message: "Please select a Store Ship-To before saving.",
      };
    }
    if (!selectedDistributionCentreShipTo?.ShipToCode) {
      return {
        isValid: false,
        message: "Please select a Distribution Centre Ship-To before saving.",
      };
    }
    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      dispatch(
        createAlert({
          status: "Error",
          message: validation.message,
        })
      );
      return;
    }

    const payload = {
      workflowLookupType,
      CustomerConnectionId: selectedCustomerConnection?.ConnectionId ?? null,
      CustomerConnectionName: selectedCustomerConnection?.Name ?? "",
      StoreShipToCode: selectedStoreShipTo?.ShipToCode ?? "",
      DistributionCentreShipToCode:
        selectedDistributionCentreShipTo?.ShipToCode ?? "",
    };

    try {
      // Edit mode, update existing record directly
      if (mode === WorkflowLookupMode.Edit && existingRow?.WorkflowLookupId) {
        await axios.put(
          `/workflowlookups/${existingRow.WorkflowLookupId}`,
          payload
        );
      } else {
        const normalize = (v) =>
          v === null || v === undefined || v === 0 ? "" : String(v);

        // Find a matching row by normalized fields
        const matchingExistingRow = rows.find(
          (x) =>
            x.WorkflowLookupType == workflowLookupType &&
            normalize(x.CustomerConnectionId) ===
              normalize(selectedCustomerConnection?.ConnectionId) &&
            normalize(x.StoreShipToCode) ===
              normalize(selectedStoreShipTo?.ShipToCode)
        );

        if (matchingExistingRow && matchingExistingRow.WorkflowLookupId) {
          const userConfirmed = await new Promise((resolve) => {
            dispatch(
              createAlert({
                status: "Warning",
                alertType: "result",
                title: "Update Overlapping Record",
                message:
                  "An Implied DC already exists for this combination. Do you want to update it?",
                buttonLabel: "Update",
                onButtonClick: () => resolve(true),
                buttonLabel2: "Cancel",
                onButtonClick2: () => resolve(false),
              })
            );
          });
          if (!userConfirmed) {
            return;
          }
          await axios.put(
            `/workflowlookups/${matchingExistingRow.WorkflowLookupId}`,
            payload
          );
        } else {
          await axios.post("/workflowlookups", payload);
        }
      }

      if (fetchData) fetchData();
      handleClose();
    } catch (e) {
      dispatch(
        createAlert({
          status: "Error",
          message: e.message,
        })
      );
    }
  };

  const handleClose = () => {
    setSelectedCustomerConnection(null);
    setSelectedStoreShipTo(null);
    setSelectedDistributionCentreShipTo(null);
    setCustomerConnectionShipTos([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        className="add-crossref-dialog-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {mode === WorkflowLookupMode.Edit
            ? "Edit Implied DC"
            : "Add Implied DC"}
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Box sx={{ ml: 3 }}>
          {/* Customer Connection */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Customer Connection:
            </Typography>
            <Autocomplete
              options={customerConnections}
              loading={!customersLoaded}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.Name
              }
              value={selectedCustomerConnection}
              onChange={(_, newValue) => {
                setSelectedCustomerConnection(newValue || null);
                setSelectedStoreShipTo(null);
                setSelectedDistributionCentreShipTo(null);
              }}
              disabled={mode === WorkflowLookupMode.Edit}
              renderInput={(params) => (
                <TextField
                  sx={{ minWidth: 350, height: 45 }}
                  {...params}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          {/* Store Ship To Code */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Store Ship To Code:
            </Typography>
            <Autocomplete
              options={customerConnectionShipTos.filter(
                (opt) =>
                  opt.ShipToCode !==
                  selectedDistributionCentreShipTo?.ShipToCode
              )}
              getOptionLabel={(opt) => {
                return codeFirstLabel(opt.ShipToCode, opt.ShipToName);
              }}
              value={selectedStoreShipTo}
              onChange={(_, newValue) => setSelectedStoreShipTo(newValue || "")}
              disabled={!selectedCustomerConnection}
              loading={shipTosLoading}
              loadingText="Loading..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          {/* Distribution Centre Ship To Code */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Distribution Centre Ship To Code:
            </Typography>
            <Autocomplete
              options={customerConnectionShipTos.filter(
                (opt) => opt.ShipToCode !== selectedStoreShipTo?.ShipToCode
              )}
              getOptionLabel={(opt) => {
                return codeFirstLabel(opt.ShipToCode, opt.ShipToName);
              }}
              value={selectedDistributionCentreShipTo}
              onChange={(_, newValue) =>
                setSelectedDistributionCentreShipTo(newValue || "")
              }
              disabled={!selectedCustomerConnection}
              loading={shipTosLoading}
              loadingText="Loading..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton onClick={handleSubmit}>
          {mode === WorkflowLookupMode.Edit ? "Save" : "Add"}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddImpliedDCPopup;
